import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ApexCharts from "react-apexcharts";
import { UncontrolledTooltip } from "reactstrap";
import s from "./Profile.module.scss";
import { ADMIN_AUDIT_LOGS_ME, API_TIMEOUT, LIST_ADMIN_TF } from "../../utils";

import TulufaliLogo from "../../components/Icons/tulufali_logo.png";
import AvatarGroup from "../../components/AvatarGroup/AvatarGroup";

const defaultAvatar =
  "https://kalkulatorgizi.rotendaokab.go.id/foto/calorie.png";

const AKSES_MENU_MAP = {
  Dashboard: { link: "/template/dashboard", tooltip: "Beranda utama dan ringkasan data bantuan sosial" },
  "Data Bantuan": { link: "/admin/bantuan", tooltip: "Daftar jenis bantuan dan data bansos per tahun" },
  "Input Penerima": { link: "/admin/penerima/new", tooltip: "Input data penerima bantuan baru" },
  "Progres Input": { link: "/template/progressinput", tooltip: "Memantau progres input data penerima per bantuan" },
  "SK Bupati": { link: "/template/progressSK", tooltip: "Status dan upload dokumen SK Bupati per bantuan" },
  Evaluasi: { link: "/template/evaluasi", tooltip: "Evaluasi penerima bantuan (Hijau/Orange/Merah/Hitam)" },
  Analitiks: { link: "/template/analitik", tooltip: "Analisis data miskin ekstrim, fasilitas, dan penerima" },
  Desil: { link: "/template/desil", tooltip: "Data desil kesejahteraan per desa dan kecamatan, cari individu" },
  "Kelola Admin": { link: "/admin/listadmin", tooltip: "Kelola akun admin Tulu Fali (Super Admin)" },
  "Kunci Aturan": { link: "/admin/kunci/kunciaturan", tooltip: "Pengaturan kunci input berdasarkan aturan" },
};

const getAksesByPeran = (peran) => {
  const p = (peran || "").toLowerCase();
  const base = [
    "Dashboard",
    "Data Bantuan",
    "Input Penerima",
    "Progres Input",
    "SK Bupati",
    "Evaluasi",
    "Analitiks",
    "Desil",
  ];
  if (p.includes("super admin")) {
    return [...base, "Kelola Admin", "Kunci Aturan"];
  }
  return base;
};

export default function Profile() {
  const [user, setUser] = useState(null);
  const [logData, setLogData] = useState({ categories: [], series: [] });
  const [logLoading, setLogLoading] = useState(true);
  const [adminFriends, setAdminFriends] = useState([]);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user"));
    if (u) setUser(u);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLogLoading(false);
      return;
    }
    axios
      .get(ADMIN_AUDIT_LOGS_ME, {
        timeout: API_TIMEOUT,
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.success === 1 && Array.isArray(res.data.data)) {
          const raw = res.data.data;
          const dateMap = new Map();
          const today = new Date();
          for (let i = 29; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            dateMap.set(key, 0);
          }
          raw.forEach((r) => {
            const k = String(r.tanggal).slice(0, 10);
            if (dateMap.has(k)) dateMap.set(k, Number(r.jumlah) || 0);
          });
          const categories = [...dateMap.keys()];
          const series = categories.map((k) => dateMap.get(k));
          setLogData({
            categories: categories.map((c) => {
              const [y, m, d] = c.split("-");
              return `${d}/${m}`;
            }),
            series,
          });
        }
      })
      .catch(() => setLogData({ categories: [], series: [] }))
      .finally(() => setLogLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    axios
      .get(LIST_ADMIN_TF, { timeout: API_TIMEOUT })
      .then((res) => {
        if (res.data?.success === 1 && Array.isArray(res.data.data)) {
          const list = res.data.data;
          const currentUid = (user.uid || "").toString().trim();
          const withPhoto = list.filter(
            (a) =>
              a.profile &&
              String(a.profile).trim() &&
              a.profile !== "-" &&
              (currentUid ? String(a.uid || "").trim() !== currentUid : true)
          );
          const sliced = withPhoto.slice(0, 10);
          setAdminFriends(
            sliced.map((a, i) => ({
              id: `admin-friend-${a.id || a.uid || i}`,
              title: a.nama || `Admin ${i + 1}`,
              img: a.profile,
            }))
          );
        }
      })
      .catch(() => setAdminFriends([]));
  }, [user]);

  if (!user) {
    return (
      <div className={s.loading}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Memuat...</span>
        </div>
      </div>
    );
  }

  const avatar = user.avatar || defaultAvatar;
  const isSuperAdmin = (user.peran || "").toLowerCase().includes("super admin");
  const aksesList = getAksesByPeran(user.peran);

  const total30 = (logData.series || []).reduce((a, b) => a + b, 0);
  const total7 = (logData.series || []).slice(-7).reduce((a, b) => a + b, 0);

  const chartOptions = {
    series: [{ name: "Aktivitas", data: logData.series }],
    options: {
      chart: { type: "area", toolbar: { show: false }, height: 200 },
      colors: ["#2563eb"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      fill: { type: "gradient", opacity: 0.15 },
      xaxis: {
        categories: logData.categories,
        labels: { style: { colors: "#64748b", fontSize: "10px" } },
      },
      yaxis: { labels: { style: { colors: "#64748b" } } },
      tooltip: { y: { formatter: (v) => `${v} aktivitas` } },
      grid: { borderColor: "#f1f5f9", strokeDashArray: 2 },
    },
  };

  return (
    <div className={s.root}>
      <div className={s.card}>
        {/* Cover + Avatar */}
        <div className={s.cover} />
        <div className={s.profil}>
          <img src={avatar} alt={user.nama} className={s.avatar} />
          <h1 className={s.nama}>{user.nama}</h1>
          {user.dinas && <p className={s.dinas}>{user.dinas}</p>}
          {user.peran && (
            <span
              className={`${s.badge} ${isSuperAdmin ? s.badgeRed : s.badgeBlue}`}
            >
              {user.peran}
            </span>
          )}
        </div>

        {/* Stat cards */}
        <div className={s.stats}>
          <div className={s.statCard}>
            <span className={s.statLabel}>Aktivitas 30 hari</span>
            <span className={`${s.statValue} ${s.blue}`}>{total30}</span>
          </div>
          <div className={s.statCard}>
            <span className={s.statLabel}>Aktivitas 7 hari</span>
            <span className={`${s.statValue} ${s.green}`}>{total7}</span>
          </div>
          <div className={s.statCard}>
            <span className={s.statLabel}>Akses menu</span>
            <span className={`${s.statValue} ${s.red}`}>{aksesList.length}</span>
          </div>
        </div>

        {/* Informasi pribadi - full width + logo & tagline */}
        <div className={s.infoSection}>
          <div className={s.brand}>
            <img src={TulufaliLogo} alt="Tulu Fali" className={s.logoImg} />
            <div className={s.brandText}>
              <span className={s.brandTitle}>Tulu Fali <span className={s.version}>2.0</span></span>
              <span className={s.brandSubtitle}>
                Terintegrasi <span className={s.subtitleBadge}>Esa Office</span>
              </span>
            </div>
          </div>
          <p className={s.roleDesc}>
            Sebagai <strong>{user.peran || "pengguna"}</strong>, Anda berperan dalam memastikan keadilan dalam distribusi bantuan sosial demi penanggulangan kemiskinan ekstrim.
          </p>
          <div className={s.infoGrid}>
            <div className={s.infoItem}>
              <span className={s.infoLabel}>Nama</span>
              <span className={s.infoVal}>{user.nama || "-"}</span>
            </div>
            <div className={s.infoItem}>
              <span className={s.infoLabel}>Dinas</span>
              <span className={s.infoVal}>{user.dinas || "-"}</span>
            </div>
            <div className={s.infoItem}>
              <span className={s.infoLabel}>Peran</span>
              <span className={`${s.infoVal} ${s.blue}`}>{user.peran || "-"}</span>
            </div>
            {user.uid && (
              <div className={s.infoItem}>
                <span className={s.infoLabel}>ID Akun</span>
                <span className={`${s.infoVal} ${s.mono}`}>{user.uid}</span>
              </div>
            )}
          </div>
        </div>

        {/* Grafik - full width */}
        <div className={s.chartSection}>
          <h3 className={s.sectionTitle}>
            Log aktivitas <span className={s.red}>1 bulan</span> terakhir
          </h3>
          {logLoading ? (
            <div className={s.chartLoading}>
              <div className="spinner-border spinner-border-sm text-primary" />
            </div>
          ) : logData.series.length > 0 ? (
            <ApexCharts
              series={chartOptions.series}
              options={chartOptions.options}
              type="area"
              height={220}
            />
          ) : (
            <div className={s.chartEmpty}>Belum ada data log</div>
          )}
        </div>

        {/* Akses menu - full width, di bawah grafik, clickable + tooltip */}
        <div className={s.aksesSection}>
          <h3 className={s.sectionTitle}>
            Akses sebagai <span className={s.green}>{user.peran || "Pengguna"}</span>
          </h3>
          <div className={s.chips}>
            {aksesList.map((item, i) => {
              const meta = AKSES_MENU_MAP[item];
              const id = `menu-${i}-${item.replace(/\s/g, "-")}`;
              if (meta) {
                return (
                  <span key={i}>
                    <Link
                      to={meta.link}
                      className={s.chipLink}
                      id={id}
                    >
                      {item}
                    </Link>
                    <UncontrolledTooltip target={id} placement="top">
                      {meta.tooltip}
                    </UncontrolledTooltip>
                  </span>
                );
              }
              return <span key={i} className={s.chip}>{item}</span>;
            })}
          </div>
        </div>

        {/* Sesama Admin Tulu Fali - foto profil */}
        {adminFriends.length > 0 && (
          <div className={s.friendsSection}>
            <h3 className={s.sectionTitle}>Sesama Admin Tulu Fali</h3>
            <div className={s.friendsWrap}>
              <div className={s.friendsAvatars}>
                <AvatarGroup
                  data={adminFriends}
                  size="lg"
                  placement="top"
                  className={s.avatarGroup}
                />
              </div>
              <div className={s.friendsMeta}>
                <p className={s.friendsCount}>{adminFriends.length} Admin</p>
                <p className={s.friendsHint}>Dengan foto profil</p>
              </div>
            </div>
          </div>
        )}

        {/* Quick links */}
        <div className={s.footer}>
          <Link to="/template/dashboard">Dashboard</Link>
          <Link to="/admin/bantuan">Bantuan</Link>
          <Link to="/template/desil/cari-individu">Cari Individu</Link>
        </div>
      </div>
    </div>
  );
}
